import { ModelFilterConfig, ModelOption } from "src/genai/ModelSelector/types";

const DEFAULT_QUOTA_DISABLED_REASON =
  "This feature requires the AI Workbench package. Please contact your Solution Engineer to obtain access to this model.";

/**
 * Filters models based on quotas, feature flags, and WIP status.
 * Also marks models as disabled based on quota availability.
 *
 * This centralizes the filtering logic so it can be reused across different
 * model selectors (aiNode, agents).
 */
export const filterModels = (
  models: ModelOption[],
  config: ModelFilterConfig,
): ModelOption[] => {
  return models
    .filter((model) => {
      // Always show the currently selected model
      if (model.id === config.selectedModelId) {
        return true;
      }

      // Hide models with visibility set to false (e.g. feature flagged)
      return config.modelVisibility?.[model.id] !== false;
    })
    .map((model) => {
      // If no quotas provided, don't apply quota-based filtering
      if (!config.quotas) {
        return model;
      }

      // Apply quota-based availability
      const quota = config.quotas[model.id];
      const isAvailable =
        quota?.tokenQuota !== undefined && quota.tokenQuota !== 0;

      // Don't override if already disabled
      if (model.disabled) {
        return model;
      }

      return {
        ...model,
        disabled: !isAvailable,
        disabledReason: !isAvailable
          ? (config.quotaDisabledReason ?? DEFAULT_QUOTA_DISABLED_REASON)
          : undefined,
      };
    });
};
